import { Stack } from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes";
import type { DimensionField } from "../../../../../../../../../api/types/svTypes";
import { selectFieldsForCalculation } from "../../../../../store/selectors/fieldsStateSelectors";
import { selectDimensions } from "../../../../../store/selectors/metaDataSelectors";
import { DimensionNames } from "../../../../../utilities/DimensionNames";
import { isEligibleDateScopeField } from "../../../../../utilities/fieldsHelper";
import CustomLabel from "../../../../viewer/common/fields/CustomLabel";
import FieldOptionDeleteButton from "../../../../viewer/common/fields/FieldOptionDeleteButton";
import FieldOptionsDivider from "../../../../viewer/common/fields/FieldOptionsDivider";
import DateToShowSelect from "./controls/DateToShowSelect";
import DateTypeSelect from "./controls/DateTypeSelect";
import ReferenceCalculatedFieldSelect from "./controls/ReferenceCalculatedFIeldSelect";
import { aggregationToDateCalculation, dateCalculationToAggregation } from "./utilities/dateCalculationTypes";

interface Props {
  field: DimensionField;
  saveChanges: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  onRemove: () => void;
  cancel: () => void;
  readonly: boolean;
}

const dateFieldNames: string[] = [DimensionNames.PostingDate, DimensionNames.TransactionDate];

const DateAggregationFieldOptions = ({ field, saveChanges, onRemove, cancel, readonly }: Props) => {
  const allFieldsForCalculation = useSelector(selectFieldsForCalculation);
  const dimensions = useSelector(selectDimensions);

  const eligibleFields = useMemo(
    () => allFieldsForCalculation.filter(isEligibleDateScopeField),
    [allFieldsForCalculation]
  );
  const dateFields = useMemo(
    () =>
      dateFieldNames
        .map((name) => dimensions.find((d) => d.name === name))
        .filter((d): d is NonNullable<typeof d> => d != null)
        .map((d) => ({ name: d.name, caption: d.caption })),
    [dimensions]
  );

  const handleRemove = () => {
    onRemove();
    cancel();
  };

  return (
    <>
      <CustomLabel
        customFieldValue={field.config.customLabel}
        originalFieldValue={field.meta.caption}
        updateCustomFieldValue={(customLabel) => saveChanges({ customLabel }, false)}
        readonly={readonly}
      />
      <FieldOptionsDivider />
      <Stack spacing={2} sx={{ py: 1.5, px: 2 }}>
        <DateTypeSelect
          selectedType={aggregationToDateCalculation(field.config.aggregation)}
          onSelectedTypeChange={(type) => saveChanges({ aggregation: dateCalculationToAggregation(type) }, false)}
          readonly={readonly}
        />
        <DateToShowSelect
          selectedDateField={field.config.name}
          onSelectedDateFieldChange={(name) => saveChanges({ name }, false)}
          dateFields={dateFields}
          readonly={readonly}
        />
        <ReferenceCalculatedFieldSelect
          selectedFieldGuid={field.config.dependsFromFieldId ?? ""}
          onSelectedFieldGuidChange={(guid) => saveChanges({ dependsFromFieldId: guid }, false)}
          calculatedFields={eligibleFields}
          selectedFieldGuidError={undefined}
          readonly={readonly}
        />
      </Stack>
      <FieldOptionsDivider />
      {!readonly && <FieldOptionDeleteButton onRemove={handleRemove} />}
    </>
  );
};

export default DateAggregationFieldOptions;
